import { Fragment } from "react"
import { observer } from "mobx-react"
import Head from "next/head"
import { useAmp } from "next/amp"
import { createDocumentTitle } from "@lib/document"
import { useStore } from "@features/common"
import { dateFormater } from "@lib/date"
import { getFaqPageJSONLDSchema, getJSONLDSchema } from "@lib/meta"
import { serverPath } from "@lib/server"
import { DocumentSerializerOutput } from "@serializers/documents"

import { CommonMeta } from "./common-meta"

interface AlternateLink {
  lang: string
  url: string
}

export interface MetaProps {
  title?: string
  metaTitle?: string
  metaImage?: string
  description?: string
  metaUrl?: string
  metaAuthor?: string
  metaAuthorImg?: string
  metaPublishedTime?: Date | null
  metaModifiedTime?: Date | null
  alternateLinks?: {
    lang?: string | null
    url?: string | null
  }[]
  isArticle?: boolean
  FAQData?: DocumentSerializerOutput[] | null
  seoH1?: string
}

const OGSiteType = Object.freeze({
  article: "article" as "article",
  website: "website" as "website",
})

export const Meta = observer(function Meta(props: MetaProps) {
  const { GlobalModel } = useStore()
  const {
    title = "Pure",
    metaImage,
    metaTitle,
    description,
    metaUrl,
    metaAuthor,
    metaAuthorImg,
    metaPublishedTime,
    metaModifiedTime,
    alternateLinks = [],
    isArticle,
    FAQData,
    seoH1,
  } = props

  function resolveMetaTitle() {
    if (metaTitle) return metaTitle
    return title
      ? createDocumentTitle(title, GlobalModel.config.siteName)
      : GlobalModel.config.siteName
  }

  const isAmp = useAmp()
  const resolvedMetaTitle = resolveMetaTitle()
  const resolvedMetaDescription = description || resolvedMetaTitle

  const pageURL = new URL(metaUrl || GlobalModel.currentUrl)
  pageURL.searchParams.delete("amp")
  const ampPageURL = new URL(metaUrl || GlobalModel.currentUrl)
  ampPageURL.searchParams.set("amp", "1")

  const resolveImage =
    metaImage ||
    pageURL.origin + serverPath("/static/images/logos/pure_landing_preview.png")
  const resolveMetaAuthor = metaAuthor || GlobalModel.config.siteName
  const OGType = isArticle ? OGSiteType.article : OGSiteType.website

  const datePublished = isAmp
    ? (metaPublishedTime
        ? new Date(metaPublishedTime)
        : new Date()
      ).toISOString()
    : dateFormater(
        metaPublishedTime ? new Date(metaPublishedTime) : new Date(),
        "dd.MM.yyyy HH:mm:ss",
        GlobalModel.lang,
      )
  const dateModified = isAmp
    ? (metaModifiedTime ? new Date(metaModifiedTime) : new Date()).toISOString()
    : dateFormater(
        metaModifiedTime ?? new Date(),
        "dd.MM.yyyy HH:mm:ss",
        GlobalModel.lang,
      )

  const JSONLDSchema = getJSONLDSchema({
    isAmp,
    authorImg: metaAuthor,
    authorName: metaAuthorImg,
    dateModified: dateModified,
    datePublished: datePublished,
    description: resolvedMetaDescription,
    headline: title,
    image: resolveImage,
    isArticle: isArticle,
    name: resolvedMetaTitle,
    siteName: GlobalModel.config.siteName,
    url: pageURL.href,
  })

  const FaqJSONLDSChema = FAQData ? getFaqPageJSONLDSchema(FAQData) : null

  const filteredAlternateLinks = alternateLinks.reduce<AlternateLink[]>(
    (acc, link) => {
      if (link.lang && link.url) return acc.concat(link as AlternateLink)
      return acc
    },
    [],
  )

  const ampPageTag = isArticle ? (
    <link rel="amphtml" href={ampPageURL.href} />
  ) : null

  return (
    <Head>
      <title>{seoH1 || resolvedMetaTitle}</title>

      <CommonMeta
        lang={GlobalModel.lang}
        siteName={GlobalModel.config.siteName}
      />

      <meta property="og:type" content={OGType} />
      <meta property="og:title" content={resolvedMetaTitle} />
      <meta name="twitter:title" content={resolvedMetaTitle} />

      <meta name="description" content={resolvedMetaDescription} />
      <meta property="og:description" content={resolvedMetaDescription} />
      <meta name="twitter:description" content={resolvedMetaDescription} />
      <meta name="keywords" content="" />
      <meta property="og:image" content={resolveImage} />
      <meta property="og:image:secure_url" content={resolveImage} />
      <meta name="twitter:image" content={resolveImage} />
      <meta name="twitter:card" content="summary_large_image" />

      <meta property="og:url" content={pageURL.href} />
      <meta property="url" content={pageURL.href} />

      {isAmp ? <link rel="canonical" href={pageURL.href} /> : ampPageTag}

      <Fragment>
        {filteredAlternateLinks.map(({ lang, url }) => (
          <link key={lang} rel="alternate" hrefLang={lang} href={url} />
        ))}
      </Fragment>

      <meta name="author" content={resolveMetaAuthor} />

      {OGType === OGSiteType.article ? (
        <Fragment>
          <meta property="article:author" content={resolveMetaAuthor} />
          <meta property="article:published_time" content={datePublished} />
          <meta property="article:modified_time" content={dateModified} />
        </Fragment>
      ) : null}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSONLDSchema || "" }}
      />

      {FaqJSONLDSChema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: FaqJSONLDSChema }}
        />
      )}
    </Head>
  )
})
